/**
 * The one place the console turns an API asset path into something an `<img>` can load.
 *
 * Everything else in this app reaches the API through the typed transport, which attaches the
 * bearer token and prefixes `/v1` for you. A picture cannot: `<img src>` is a plain browser
 * fetch with no transport in front of it, so origin and prefix have to be composed by hand,
 * and every place that composes them by hand is a place that can compose them wrong.
 *
 * It has been composed wrong before, in both directions. A helper on the mobile receipt built
 * an absolute URL without the `/v1` prefix against a path that had none either, so every bank
 * logo and biller icon 404'd and fell back to a placeholder without saying anything (e813cb7).
 * The console's own bill-service icons carry a defensive check for the opposite case, because
 * the mock transport hands back paths that *do* carry the prefix while the real API hands back
 * paths that do not.
 *
 * So this takes either shape and produces one correct URL, and returns null — never a
 * half-built string — for the two states where there is nothing to load.
 */

import { API_PREFIX } from '../contracts/routes.js';

/**
 * Compose an absolute, browser-loadable URL for an asset the API serves.
 *
 * `origin` is `API_BASE_URL`: the configured API origin, or the empty string when the console
 * is running on its in-memory mock transport. Empty means there is no API to ask, which is a
 * different thing from an asset that failed to load, and callers must render it differently —
 * hence null rather than a same-origin path that would 404 against the static host.
 *
 * `path` is what the API returned. It may or may not already carry the `/v1` prefix; both are
 * accepted, and neither is doubled.
 *
 * `version` is the asset's own `updated_at`, which becomes the `?v=` cache-buster. A replaced
 * image is then a different URL, so a cached one can never be stale. Anything that is not a
 * finite number is dropped rather than written out as `v=NaN` — `Date.parse` of a malformed
 * timestamp is a real way to get there, and a URL carrying `NaN` still loads, so the mistake
 * would be invisible until someone wondered why the browser never refetched.
 */
export function assetUrl(
  origin: string,
  path: string | null | undefined,
  version?: string | number | null,
): string | null {
  if (origin === '') return null;
  if (typeof path !== 'string') return null;

  const trimmed = path.trim();
  if (trimmed === '') return null;

  // A path the API built, not a URL a caller invented. Anything absolute or protocol-relative
  // would point this console's `<img>` at a host nobody here chose, so it is refused rather
  // than concatenated onto the origin and quietly ignored.
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

  const prefixed =
    trimmed === API_PREFIX || trimmed.startsWith(`${API_PREFIX}/`)
      ? trimmed
      : `${API_PREFIX}${trimmed}`;

  const stamp = versionStamp(version);
  if (stamp === null) return `${origin}${prefixed}`;

  // The path may already carry a query — `billServiceIconPath` and friends stamp their own
  // `?v=`. Appending a second `?` would make the whole thing unparseable, so this joins with
  // the right separator and leaves an existing stamp alone.
  if (prefixed.includes('?')) return `${origin}${prefixed}`;
  return `${origin}${prefixed}?v=${stamp}`;
}

/** The `?v=` value, or null when there is nothing usable to stamp with. */
function versionStamp(version: string | number | null | undefined): string | null {
  if (version === null || version === undefined) return null;
  if (typeof version === 'number') return Number.isFinite(version) ? String(version) : null;

  const trimmed = version.trim();
  if (trimmed === '') return null;

  // An ISO timestamp is the usual argument. Reduced to epoch milliseconds so the stamp is a
  // short, URL-safe integer rather than a percent-encoded date, and so it matches the stamps
  // the API writes into `/config` for the same assets.
  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) ? String(parsed) : null;
}
